/* global $ */ // Declare $ as a global variable for jQuery usage
import Sidebar from '@/components/Home_Sidebar/sidebarView.vue';
import Navbar from '@/components/Home_Navbar/NavbarView.vue';
import { get_token, getCookie } from '@/router/index.js';
import Swal from 'sweetalert2';

export default {
  name: 'PermissionView',
  components: { Sidebar, Navbar },

  data: () => ({
    csrfToken: '',
    dataTable: null,

    // lists
    permissions: [],

    // UI
    filter_type: 'all',
    permission_loading: false,

    // Add modal
    permission_name: '',
    permission_description: '',

    // Manage modal
    id: null,
    name: '',
    description: '',
    selected_main_feature_id: null,
    selected_sub_feature_id: null,
    selected_action_id: null,
    selected_action_ids: [], 
    original_action_ids: [],
    
    features_hierarchy: [],
    opened_accordion: null,
  }),

  computed: {
    filtered_permissions() {
      if (this.filter_type === 'active') return this.permissions.filter(p => p.is_active);
      if (this.filter_type === 'inactive') return this.permissions.filter(p => !p.is_active);
      return this.permissions;
    },
    active_permissions_count() {
      return this.permissions.filter(p => p.is_active).length;
    },
    inactive_permissions_count() {
      return this.permissions.filter(p => !p.is_active).length;
    },
    // Check if there are changes
    has_no_changes() {
      const current_action_ids = Reflect.get(this, 'selected_action_ids') || [];
      const original_action_ids = Reflect.get(this, 'original_action_ids') || [];

      const sorted_current = [...current_action_ids].sort((a, b) => a - b);
      const sorted_original = [...original_action_ids].sort((a, b) => a - b);

      if (sorted_current.length !== sorted_original.length) {
        return false;
      }

      return sorted_current.every((id, index) => {
        return id === Reflect.get(sorted_original, index);
      });
    }

  },

  mounted() {
    this.csrfToken = getCookie('csrftoken');
    this.get_permissions();
  },

  methods: {
    // ---------------- helpers ----------------
    require_token_or_redirect() {
      const token = get_token();
      if (!token) {
        this.$router.push({ name: 'Login' });
        return null;
      }
      return token;
    },
    initialize_data_table() {
      this.$nextTick(() => {
        const tableEl = $('#permissions_table');
        if (!tableEl.length || !$.fn.DataTable) return;
        if ($.fn.DataTable.isDataTable(tableEl)) {
          tableEl.DataTable().destroy();
        }
        this.dataTable = tableEl.DataTable({
          pageLength: 10,
          lengthChange: false,
          searching: true,
          ordering: true,
          paging: true,
          info: true,
          autoWidth: false
        });
      });
    },

    normalize_permission_data: (pd = {}) => ({
      id: pd.id ?? null,
      name: pd.name ?? '',
      description: pd.description ?? '',
      is_active: pd.is_active,
      main_feature_id: pd.main_feature_id ?? null,
      sub_feature_id: pd.sub_feature_id ?? null,
      sub_feature_crud_id: pd.sub_feature_crud_id ?? null,
      main_feature_name: pd.main_feature_name ?? '',
      sub_feature_name: pd.sub_feature_name ?? '',
      action_name: pd.action_name ?? '',
    }),

    extract_main_feature_ids(assignment_data) {
      const main_feature_ids = assignment_data
        .map(a => a.main_feature_id)
        .filter(id => id);
      
      return Array.from(new Set(main_feature_ids));
    },

    // Validation helper to check if string contains at least one letter or number
    is_valid_text(text) {
      if (!text || typeof text !== 'string') {
        return false;
      }
      const trimmed = text.trim();
      if (trimmed.length === 0) {
        return false;
      }
      const has_alphanumeric = /[a-zA-Z0-9]/.test(trimmed);

      return has_alphanumeric;
    },

    // Validate permission name (required, must have alphanumeric)
    validate_permission_name(name) {
      if (!this.is_valid_text(name)) {
        this.$notyf.error('Permission name must contain at least one letter or number');
        return false;
      }

      const trimmed = name.trim();

      // Optional: Check minimum length
      if (trimmed.length < 3) {
        this.$notyf.error('Permission name must be at least 3 characters long');
        return false;
      }

      // Optional: Check maximum length
      if (trimmed.length > 100) {
        this.$notyf.error('Permission name must not exceed 100 characters');
        return false;
      }

      return true;
    },

    async get_permissions() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_all_permissions/`;

      try {
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        });

        if (!res.ok) {
          const data = await res.json();
          this.$notyf.error(data.message || `Failed to fetch permissions: HTTP ${res.status}`);
          return;
        }
        
        const data = await res.json();

        if (data.status === 'success') {
          const permission_list = data.data || this.get_empty_array();
          this.permissions = permission_list.map(p => this.normalize_permission_data(p));
          this.initialize_data_table();
        } else {
          this.$notyf.info(data.message || 'Failed to fetch permissions');
        }
      } catch (e) {
        this.$notyf.error(`Error fetching permissions: ${String(e)}`);
      }
    },

    async get_existing_main_features() {
      const token = this.require_token_or_redirect();
      if (!token) return null;

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_permission_assignments/${this.id}/`;
      
      try {
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        });

        if (!res.ok) {
          const error_data= await res.json().catch(() => ({ message: 'Unknown error' }));
          this.$notyf.error(error_data.message || 'Failed to get existing assignments');
          return null;
        }
        
        const data = await res.json();
        if (!data.data) {
          return this.get_empty_array();
        }

        return this.extract_main_feature_ids(data.data);

      } catch (e) {
        this.$notyf.error(`Error getting existing assignments: ${String(e)}`);
        return null;
      }
    },

    get_empty_array() {
      return [];
    },

    async load_features_hierarchy() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      this.permission_loading = true;
      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_features_hierarchy/`;
      
      try {
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.status === 'success') {
          this.features_hierarchy = data.data || [];
        } else {
          this.$notyf.error(data.message || 'Failed to load features');
        }
      } catch (e) {
        this.$notyf.error(`Error loading features: ${String(e)}`);
      } finally {
        this.permission_loading = false;
      }
    },

    async load_permission_assignments(permission_id) {
      const token = this.require_token_or_redirect();
      if (!token) return;

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/get_permission_assignments/${permission_id}/`;
      
      try {
        const res = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          }
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        if (data.status === 'success') {
          const assignments = data.data || [];
          
          // Extract all assigned action IDs
          const action_ids = assignments.map(a => a.sub_feature_crud_id).filter(id => id);
          this.selected_action_ids = action_ids;
          this.original_action_ids = [...action_ids];
          
          // Open first accordion if there are assignments
          if (assignments.length > 0) {
            const main_feature_ids = [...new Set(assignments.map(a => a.main_feature_id).filter(id => id))];
            if (main_feature_ids.length > 0) {
              this.opened_accordion = main_feature_ids[0];
            }
          }
          
          // Set action_id to first one for backward compatibility
          if (action_ids.length > 0) {
            this.selected_action_id = action_ids[0];
          }
        } else {
          // No assignments yet is not an error
          this.$notyf.error(`No assignments found for permission.`);
        }
      } catch (e) {
        // Don't show error toast for new permissions with no assignments
        if (!e.message.includes('404')) {
          this.$notyf.error(`Error loading assignments: ${String(e)}`);
        }
      }
    },

    // Toggle accordion open/close
    toggle_accordion(mainId) {
      if (this.opened_accordion === mainId) {
        this.opened_accordion = null;
      } else {
        this.opened_accordion = mainId;
      }
    },

    // Check if an action is selected
    is_action_selected(action_id) {
      return this.selected_action_ids.includes(action_id);
    },

    // Check if a sub-feature is selected (any of its actions are selected)
    is_sub_feature_selected(sub_id) {
      for (const main of this.features_hierarchy) {
        const sub = (main.sub_features || []).find(s => s.id === sub_id);
        if (!sub) continue;

        const sub_action_ids = (sub.crud_actions || []).map(a => a.id);
        if (sub_action_ids.some(aid => this.selected_action_ids.includes(aid))) {
          return true;
        }
      }
      return false;
    },

    // Handle sub-feature toggle - allows multiple sub-features across main features
    handle_subfeature_toggle(event, main_id, sub_id) {
      event.stopPropagation();

      const main = this.features_hierarchy.find(m => m.id === main_id);
      if (!main) return;
      
      const sub = main.sub_features?.find(s => s.id === sub_id);
      if (!sub || !sub.crud_actions || sub.crud_actions.length === 0) return;

      const sub_action_ids = sub.crud_actions.map(a => a.id);
      const has_any_selected = sub_action_ids.some(aid => this.selected_action_ids.includes(aid));

      if (has_any_selected) {
        // Uncheck - remove all actions from this sub-feature
        this.selected_action_ids = this.selected_action_ids.filter(
          aid => !sub_action_ids.includes(aid)
        );
        
        if (this.selected_action_ids.length === 0) {
          this.selected_sub_feature_id = null;
          this.selected_action_id = null;
        } else {
          this.selected_action_id = this.selected_action_ids[0];
        }
      } else {
        // Check - add all actions from this sub-feature
        this.selected_action_ids = [
          ...this.selected_action_ids,
          ...sub_action_ids
        ];
        
        this.selected_sub_feature_id = sub_id;
        this.selected_action_id = sub_action_ids[0];
      }
    },

    // Handle individual action toggle - maintains selections across main features
    handle_action_toggle(event, main_id, sub_id, action_id) {
      event.stopPropagation();
      
      const index = this.selected_action_ids.indexOf(action_id);
      if (index > -1) {
        this.selected_action_ids.splice(index, 1);
        
        if (this.selected_action_ids.length === 0) {
          this.selected_sub_feature_id = null;
          this.selected_action_id = null;
        } else {
          this.selected_action_id = this.selected_action_ids[0];
        }
      } else {
        this.selected_action_ids.push(action_id);
        this.selected_sub_feature_id = sub_id;
        this.selected_action_id = action_id;
      }
    },

    async open_manage_modal(permission) {

      this.id = permission.id;
      this.name = permission.name || '';
      this.description = permission.description || '';

      // Reset selection state for Manage modal
      this.selected_action_ids = [];
      this.original_action_ids = [];
      this.selected_sub_feature_id = null;
      this.selected_action_id = null;
      this.opened_accordion = null;

      // Load features hierarchy first
      await this.load_features_hierarchy();
      
      // Load all existing assignments for this permission
      await this.load_permission_assignments(permission.id);
    },

    // Current mapping for manage - groups action IDs by main feature
    _current_mapping_for_manage() {
      const action_ids = this.selected_action_ids;
      if (!action_ids?.length) {
        // ALLOW EMPTY TO SUPPORT UNASSIGN ALL
        this.current_mapping = {
          grouped_by_main: {},
          all_action_ids: [],
          sub_feature_crud_id: null
        };
        return;
      }

      const grouped_by_main = {};

      action_ids.forEach(action_id => {
        this.features_hierarchy.forEach(main => {
          (main.sub_features || []).forEach(sub => {
            if (sub.crud_actions?.some(a => a.id === action_id)) {
              if (!grouped_by_main[main.id]) grouped_by_main[main.id] = [];
              grouped_by_main[main.id].push(action_id);
            }
          });
        });
      });

      // Instead of returning, assign to a property
      this.current_mapping = {
        grouped_by_main,
        all_action_ids: action_ids,
        sub_feature_crud_id: action_ids[0]
      };
    },

    // ---------------- create ----------------
    async add_permission() {
      const token = this.require_token_or_redirect();
      if (!token) return;

      // Validate permission name
      if (!this.validate_permission_name(this.permission_name)) {
        return;
      }

      const name = (this.permission_name || '').trim();
      if (!name) {
        this.$notyf.info('Please enter a permission name');
        return;
      }

      this.permission_loading = true;
      try {       
        const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/create_permission/`;
        const payload = {
          name,
          description: this.permission_description || '',
        };

        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          },
          body: JSON.stringify(payload)
        });

        const data = await res.json();

        if (data.status === 'success') {
          this.$notyf.success('Permission created.');
          await this.get_permissions();
          this.permission_name = '',
          this.permission_description= ''
          window.location.reload();
        } else {
          this.$notyf.error(data.message || 'Failed to create permission');
        }
      } catch (e) {
        this.$notyf.error(`Error creating permission: ${String(e)}`);
      } finally {
        this.permission_loading = false;
      }
    },

    // Update name/description with multiple action IDs
    async update_permission_basic_info() {
      if (!this.id) return;
      // Validate permission name
      if (!this.validate_permission_name(this.name)) {
        return;
      }

      const token = this.require_token_or_redirect();
      if (!token) return;

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/update_permission/`;
      const body = {
        permission_id: this.id,
        name: this.name || '',
        description: this.description || ''
      };

      this._current_mapping_for_manage();
      const current = this.current_mapping;
      if (current) {
        body.main_feature_id = current.main_feature_id;
        body.action_ids = current.action_ids;
        body.sub_feature_crud_id = current.sub_feature_crud_id;
      }

      this.permission_loading = true;
      try {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          },
          body: JSON.stringify(body)
        });

        const data = await res.json();

        if (data.status === 'success') {
          this.$notyf.success('Permission info updated.');
          window.location.reload();
        } else {
          this.$notyf.error(data.message || 'Failed to update permission');
        }
      } catch (e) {
        this.$notyf.error(`Error updating permission: ${String(e)}`);
      } finally {
        this.permission_loading = false;
      }
    },

    async save_permission_assignments() {
      if (!this.id) return;

      const token = this.require_token_or_redirect();
      if (!token) return;

      this.permission_loading = true;

      try {
        this._current_mapping_for_manage();
        const current = this.current_mapping;

        const existing_main_feature_ids = await this.get_existing_main_features();
        if (existing_main_feature_ids === null) {
          return;
        }

        // Determine which main features need to be unassigned
        const selected_main_ids = Object.keys(current.grouped_by_main).map(id => parseInt(id, 10));
        const main_ids_to_unassign = existing_main_feature_ids.filter(
          id => !selected_main_ids.includes(id)
        );

        // First, unassign main features that are no longer selected
        if (main_ids_to_unassign.length > 0) {
          for (const main_id of main_ids_to_unassign) {
            const success = await this.unassign_main_feature(main_id);
            if (!success) {
              this.$notyf.error(`Failed to unassign feature`);
              this.permission_loading = false;
              return;
            }
          }
        }

        // Then, save assignments for selected main features
        if (selected_main_ids.length > 0) {
          const saved = await this.save_main_feature_assignments(current.grouped_by_main);
          if (!saved) {
            this.permission_loading = false;
            return;
          }
        }

        this.$notyf.success('Permission assignments saved successfully.');

        // Reload assignments to reflect changes
        await this.load_permission_assignments(this.id);
        await this.get_permissions();

      } catch (e) {
        this.$notyf.error(`Error saving assignments: ${String(e)}`);
      } finally {
        this.permission_loading = false;
      }
    },

    // Helper: Save assignments for multiple main features
    async unassign_main_feature(main_feature_id) {
      const token = this.require_token_or_redirect();
      if (!token) return false;

      const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/assign_features_to_permission/`;

      const body = {
        permission_id: this.id,
        name: this.name || '',
        description: this.description || '',
        main_feature_id: main_feature_id,
        action_ids: [],
        sub_feature_crud_id: null
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) {
          const error_data = await res.json().catch(() => ({ message: 'Unknown error' }));
          console.error('Unassign error:', error_data);
          return false;
        }

        const data = await res.json();
        return data.status === 'success';

      } catch (e) {
        console.error(`Error unassigning main feature ${main_feature_id}:`, e);
        return false;
      }
    },

  // 3. Update save_main_feature_assignments to remove the unassignment logic
  // (since it's now handled separately in save_permission_assignments)
  async save_main_feature_assignments(grouped_by_main) {
    const token = this.require_token_or_redirect();
    if (!token) return false;

    const url = `${process.env.VUE_APP_API_BASE_URL}/aic_one_api/system_management/assign_features_to_permission/`;
    const main_feature_ids = Object.keys(grouped_by_main);

    // If no main features selected, return true (unassignment handled elsewhere)
    if (main_feature_ids.length === 0) {
      return true;
    }

    for (const main_feature_id_str of main_feature_ids) {
      const main_feature_id = parseInt(main_feature_id_str, 10);
      const action_ids = Reflect.get(grouped_by_main, main_feature_id_str);

      if (!action_ids || action_ids.length === 0) {
        continue; // Skip if no actions for this main feature
      }

      const body = {
        permission_id: this.id,
        name: this.name || '',
        description: this.description || '',
        main_feature_id: main_feature_id,
        action_ids: action_ids,
        sub_feature_crud_id: action_ids[0]
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Token ${token}`,
            'X-CSRFToken': this.csrfToken,
            'X-Page-URL': window.location.href
          },
          body: JSON.stringify(body)
        });

        if (!res.ok) {
          const error_data = await res.json().catch(() => ({ message: 'Unknown error' }));
          console.error('Assignment error:', error_data);
          return false;
        }

        const data = await res.json();
        if (data.status !== 'success') {
          console.error('Assignment failed:', data.message);
          return false;
        }

      } catch (e) {
        console.error(`Error saving main feature ${main_feature_id}:`, e);
        return false;
      }
    }

    return true;
  },

    // ---------------- status ----------------
    toggle_permission_status(permission) {
      const will_disable = !!permission.is_active;
      const title = will_disable ? 'Disable Permission?' : 'Enable Permission?';
      const verb = will_disable ? 'disable' : 'enable';

      Swal.fire({
        title,
        text: `Are you sure you want to ${verb} "${permission.name}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `Yes, ${verb} it!`,
        cancelButtonText: 'Cancel'
      }).then(async (result) => {
        if (!result.isConfirmed) return;

        const token = this.require_token_or_redirect();
        if (!token) return;

        const path = will_disable
          ? '/aic_one_api/system_management/deactivate_permission/'
          : '/aic_one_api/system_management/activate_permission/';

        try {
          const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}${path}`, {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Token ${token}`,
              'X-CSRFToken': this.csrfToken,
              'X-Page-URL': window.location.href
            },
            body: JSON.stringify({ permission_id: permission.id })
          });

          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const data = await res.json();
          if (data.status === 'success') {
            this.$notyf.success('Permission status updated!');
            this.get_permissions();
          } else {
            this.$notyf.error(data.message || 'Failed to update status');
          }
        } catch (e) {
          this.$notyf.error(`Error updating status: ${String(e)}`);
        }
      });
    },

    // Check if a main feature is fully selected (all of its sub-features' actions are selected)
    is_main_feature_selected(main_id) {
      const main = this.features_hierarchy.find(m => m.id === main_id);
      if (!main || !main.sub_features || main.sub_features.length === 0) {
        return false;
      }

      // Collect all action IDs from all sub-features of this main feature
      const all_action_ids = [];
      main.sub_features.forEach(sub => {
        if (sub.crud_actions && sub.crud_actions.length > 0) {
          sub.crud_actions.forEach(action => {
            all_action_ids.push(action.id);
          });
        }
      });

      // If no actions exist, return false
      if (all_action_ids.length === 0) {
        return false;
      }

      // Check if ALL actions are selected
      return all_action_ids.every(aid => this.selected_action_ids.includes(aid));
    },

    // Handle main feature toggle - select/deselect all sub-features and their actions
    handle_main_feature_toggle(event, main_id) {
      event.stopPropagation();

      const main = this.features_hierarchy.find(m => m.id === main_id);
      if (!main || !main.sub_features || main.sub_features.length === 0) {
        return;
      }

      // Collect all action IDs from all sub-features of this main feature
      const all_action_ids = [];
      main.sub_features.forEach(sub => {
        if (sub.crud_actions && sub.crud_actions.length > 0) {
          sub.crud_actions.forEach(action => {
            all_action_ids.push(action.id);
          });
        }
      });

      if (all_action_ids.length === 0) {
        return;
      }

      // Check if main feature is fully selected
      const is_fully_selected = this.is_main_feature_selected(main_id);

      if (is_fully_selected) {
        // Uncheck - remove all actions from this main feature
        this.selected_action_ids = this.selected_action_ids.filter(
          aid => !all_action_ids.includes(aid)
        );

        // Reset selection state if no actions left
        if (this.selected_action_ids.length === 0) {
          this.selected_sub_feature_id = null;
          this.selected_action_id = null;
        } else {
          this.selected_action_id = this.selected_action_ids[0];
        }
      } else {
        // Check - add all actions from this main feature (avoid duplicates)
        const new_action_ids = all_action_ids.filter(
          aid => !this.selected_action_ids.includes(aid)
        );

        this.selected_action_ids = [
          ...this.selected_action_ids,
          ...new_action_ids
        ];

        // Set the first action as selected
        this.selected_action_id = all_action_ids[0];
      }
    }
  }
};